import { client } from "../supabase";
import { computarSaldoCapex } from "./getSaldoCapexTarget";
import { claveTargetCapex, getSaldosCapexPorTarget } from "./getSaldosCapexPorTarget";

/**
 * PAGOS CapEx candidatos a asociarse a una factura recibida.
 *
 * Contexto: se pagan trabajos de CapEx a un proveedor ANTES de que exista la factura (adelantos
 * por el modal "Inversión" del edificio → path CapEx caja-pura de VincularMovimientoModal). Cada
 * pago es UNA fila `movimientos_caja`; el "quién pagó qué" vive en `plan_pagos_activos.movimiento_bancario_id`
 * (cuota→banco). La `categoria_pago` de esa caja puede nacer `pago_a_cuenta` (proveedor único, regla
 * 10.215) o `compra_activo` (fallback) — por eso se aceptan AMBAS (Fase 2, alinea con la detección
 * del badge "A" por el hilo `plan_pagos_activos`, regla 10.216; el discriminador real de "es CapEx"
 * es tener cuotas de activo detrás, no la categoría). Cuando la factura llega, el usuario asocia
 * esos pagos a la factura.
 *
 * Este util SOLO lista pagos HUÉRFANOS (todavía sin factura), con TRIPLE candado para no listar ni
 * tocar jamás un pago ya vinculado ni de otro proveedor:
 *   (1) MBF del pago vacío: su `movimiento_bancario_id` NO tiene fila en `movimiento_bancario_facturas`
 *       (Σ MBF = 0). El MBF es la única fuente de verdad de "factura de compra pagada"
 *       (documentosCosto.derivarPago) — si existe, el pago ya está atado a una factura.
 *   (2) Activos candidatos: TODOS los activos que paga el bancario (vía eventos vigentes del subledger
 *       `pagos_capex`) cumplen `estado='planificado'` + `factura_sii_recibida_id IS NULL` +
 *       `eliminado=false` (lifecycle del activo, 10.2) + TODAS sus cuotas saldadas SEGÚN EL LEDGER
 *       (`pagos_capex`, no el flag `estado`): saldo del target 0 para cada cuota (≥1 cuota).
 *   (3) Proveedor del ACTIVO: cuando se pasa `proveedorId`, TODOS los activos que paga el bancario
 *       deben tener `activos.proveedor_id === proveedorId` (el de la factura destino). El proveedor
 *       real del pago vive en el activo, NO en `movimientos_caja.entidad_id` (que puede quedar null/
 *       'otro' si el pago nació huérfano — caso Matta). Sin esto, el modal listaba pagos de cualquier
 *       proveedor (regla 10.7 — comparar por ID). Verificado en DB (10.28): activo Barbato prov=23,
 *       activo Matta prov=null.
 *
 * Selección por PAGO (el usuario tilda pagos, no activos): un pago cuyo bancario paga cuotas de un
 * activo NO candidato (parcial, ya facturado, eliminado, o de otro proveedor) queda FUERA — evita
 * listar un pago "a caballo" entre facturas. NO hay auto-matching (regla 10.4): devuelve el universo
 * candidato del proveedor; el usuario elige cuáles son de su factura por monto y contexto.
 *
 * @param {string} pais - "Chile" | "Perú" (columna `movimientos_caja.pais`).
 * @param {number} [proveedorId] - proveedor_id de la factura destino. Si se omite, no filtra por
 *   proveedor (retrocompat); el único caller (AsociarPagosCapexModal) siempre lo pasa.
 * @returns {Promise<Array<{
 *   movimientoCajaId: number,
 *   movimientoBancarioId: number,
 *   fecha: string,
 *   monto: number,
 *   moneda: string,
 *   descripcion: string,
 *   contexto: Array<{ activoId: number, tipoActivo: string, edificioId: number|null, edificioNombre: string }>,
 *   activoIds: number[],
 * }>>} pagos candidatos, ordenados por fecha ascendente.
 */
export const getPagosCapexCandidatos = async (pais, proveedorId = null) => {
  if (!pais) return [];

  // 1. Pagos CapEx del país (una fila de caja por transferencia). estado<>'anulado' + banco poblado.
  //    Se aceptan las DOS categorías con que puede nacer un pago CapEx (10.215/10.216): 'compra_activo'
  //    (fallback) y 'pago_a_cuenta' (proveedor único). El filtro real de "es CapEx" lo hace el join a
  //    plan_pagos_activos (paso 3): un pago_a_cuenta ordinario, sin cuotas de activo detrás, cae fuera.
  const { data: pagosRaw, error: errPagos } = await client
    .from("movimientos_caja")
    .select("id, movimiento_bancario_id, fecha, monto, moneda, descripcion")
    .eq("pais", pais)
    .in("categoria_pago", ["compra_activo", "pago_a_cuenta"])
    .neq("estado", "anulado")
    .not("movimiento_bancario_id", "is", null);
  if (errPagos) throw errPagos;
  const pagos = pagosRaw || [];
  if (pagos.length === 0) return [];

  const bancarioIds = [...new Set(pagos.map((p) => p.movimiento_bancario_id))];

  // 2. Candado (1): bancarios con fila MBF → ya vinculados a una factura → se descartan.
  const { data: mbfRows, error: errMbf } = await client
    .from("movimiento_bancario_facturas")
    .select("movimiento_bancario_id")
    .in("movimiento_bancario_id", bancarioIds);
  if (errMbf) throw errMbf;
  const bancariosConMBF = new Set((mbfRows || []).map((m) => m.movimiento_bancario_id));

  // 3. Eventos VIGENTES de `pagos_capex` de esos bancarios (cuota_activo) → qué activos paga cada
  //    bancario. CUTOVER Fase 2a: el mapeo bancario→activo deriva del subledger `pagos_capex` (eventos
  //    con `reversado_at IS NULL`), NO del flag `plan_pagos_activos.estado='pagada'` — deja de mezclar
  //    flag + ledger (el paso 4 ya era ledger) y captura también pagos PARCIALES (que dejan la cuota
  //    'pendiente' con el escalar `movimiento_bancario_id` sin setear, invisibles para el flag). El
  //    `target_id` de un evento cuota_activo es `plan_pagos_activos.id`; el `activo_id` se resuelve con
  //    un join por id. Para datos todo-o-nada es 1:1 con el flag (aplicado=objetivo ⟺ estado='pagada').
  const { data: eventosCapex, error: errEventos } = await client
    .from("pagos_capex")
    .select("movimiento_bancario_id, target_id")
    .eq("target_tipo", "cuota_activo")
    .is("reversado_at", null)
    .in("movimiento_bancario_id", bancarioIds);
  if (errEventos) throw errEventos;
  const cuotaTargetIds = [...new Set((eventosCapex || []).map((e) => e.target_id))];
  const cuotaToActivo = new Map(); // plan_pagos_activos.id → activo_id
  if (cuotaTargetIds.length > 0) {
    const { data: cuotasTgt, error: errCuotasTgt } = await client
      .from("plan_pagos_activos")
      .select("id, activo_id")
      .in("id", cuotaTargetIds);
    if (errCuotasTgt) throw errCuotasTgt;
    for (const c of cuotasTgt || []) cuotaToActivo.set(c.id, c.activo_id);
  }
  const activosPorBancario = new Map(); // movimiento_bancario_id → Set(activo_id)
  const activoIds = new Set();
  for (const e of eventosCapex || []) {
    const activoId = cuotaToActivo.get(e.target_id);
    if (activoId == null) continue;
    if (!activosPorBancario.has(e.movimiento_bancario_id)) activosPorBancario.set(e.movimiento_bancario_id, new Set());
    activosPorBancario.get(e.movimiento_bancario_id).add(activoId);
    activoIds.add(activoId);
  }
  if (activoIds.size === 0) return [];
  const activoIdsArr = [...activoIds];

  // 4. TODAS las cuotas de esos activos → un activo es candidato si TODAS tienen saldo del ledger 0
  //    (fully paid por el subledger pagos_capex, no por el flag `estado`). CUTOVER Fase 2b: en todo-o-nada
  //    equivale a (0 pendientes, ≥1 pagada); con parciales (F3) exige que ninguna cuota tenga saldo.
  const { data: cuotasTodas, error: errTodas } = await client
    .from("plan_pagos_activos")
    .select("id, activo_id, monto_cuota")
    .in("activo_id", activoIdsArr);
  if (errTodas) throw errTodas;
  const saldosCapex = await getSaldosCapexPorTarget(pais);
  const cuotasPorActivo = new Map(); // activo_id → { total, saldadas }
  for (const c of cuotasTodas || []) {
    const acc = cuotasPorActivo.get(c.activo_id) || { total: 0, saldadas: 0 };
    acc.total += 1;
    const aplicado = saldosCapex.get(claveTargetCapex("cuota_activo", c.id))?.aplicadoTarget || 0;
    if (computarSaldoCapex(c.monto_cuota, aplicado).saldada) acc.saldadas += 1;
    cuotasPorActivo.set(c.activo_id, acc);
  }

  // 5. Datos de los activos (estado / factura / eliminado / destino / tipo / proveedor).
  const { data: activosRows, error: errAct } = await client
    .from("activos")
    .select("id, estado, factura_sii_recibida_id, eliminado, destino_tipo, destino_id, tipo_activo, proveedor_id")
    .in("id", activoIdsArr);
  if (errAct) throw errAct;
  const activoPorId = new Map((activosRows || []).map((a) => [a.id, a]));

  // 6. Nombres de edificio para el contexto "qué compró".
  const edificioIds = [
    ...new Set(
      (activosRows || []).filter((a) => a.destino_tipo === "edificio" && a.destino_id != null).map((a) => a.destino_id)
    ),
  ];
  const nombrePorEdificio = new Map();
  if (edificioIds.length > 0) {
    const { data: edificios, error: errEd } = await client
      .from("edificios")
      .select("id, edificio")
      .in("id", edificioIds);
    if (errEd) throw errEd;
    for (const e of edificios || []) nombrePorEdificio.set(e.id, e.edificio);
  }

  // Candado (2): un activo es candidato si planificado + sin factura + no eliminado + TODAS sus cuotas
  // con saldo del ledger 0 (≥1 cuota). Candado (3): si se pasó `proveedorId`, el activo debe ser de ESE
  // proveedor (comparación por ID, 10.7) — el proveedor real del pago vive en el activo, no en la caja.
  const esActivoCandidato = (activoId) => {
    const a = activoPorId.get(activoId);
    if (!a) return false;
    if (a.estado !== "planificado" || a.factura_sii_recibida_id != null || a.eliminado) return false;
    if (proveedorId != null && a.proveedor_id !== proveedorId) return false;
    const c = cuotasPorActivo.get(activoId);
    return !!c && c.total > 0 && c.saldadas === c.total;
  };

  // Un PAGO es candidato si su bancario no tiene MBF y TODOS los activos que paga son candidatos.
  const candidatos = [];
  for (const p of pagos) {
    if (bancariosConMBF.has(p.movimiento_bancario_id)) continue;
    const activosDelBancario = [...(activosPorBancario.get(p.movimiento_bancario_id) || [])];
    if (activosDelBancario.length === 0) continue;
    if (!activosDelBancario.every(esActivoCandidato)) continue;
    candidatos.push({
      movimientoCajaId: p.id,
      movimientoBancarioId: p.movimiento_bancario_id,
      fecha: p.fecha,
      monto: Number(p.monto),
      moneda: p.moneda,
      descripcion: p.descripcion || "",
      activoIds: activosDelBancario,
      contexto: activosDelBancario.map((id) => {
        const a = activoPorId.get(id);
        return {
          activoId: id,
          tipoActivo: a?.tipo_activo || "—",
          edificioId: a?.destino_tipo === "edificio" ? a.destino_id : null,
          edificioNombre:
            a?.destino_tipo === "edificio" ? nombrePorEdificio.get(a.destino_id) || `Edificio #${a.destino_id}` : "—",
        };
      }),
    });
  }
  candidatos.sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));
  return candidatos;
};
